import { BACKEND_URL } from '@/config/api';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "./ui/dialog";
import { BookOpen, Home, Loader2, Play } from "lucide-react";
import api from "../lib/api";
import { useReaderCredits } from "../contexts/ReaderCreditContext";
import { useReaderFunnel } from "../contexts/ReaderFunnelContext";
import { apiFetch } from "../lib/apiFetch";

/**
 * WelcomeBackModal - Shown after Reader login to offer continuing their last session
 * 
 * If user has active reading sessions:
 *   - Shows "Continue Listening" option
 *   - If bookmark < 24 hours old -> goes directly to player (no recap modal)
 *   - If bookmark >= 24 hours old -> goes to /reader/my-library with recap modal
 *   - Shows "Browse Home" option -> goes to /home
 * 
 * If no active sessions:
 *   - Automatically redirects to /home
 */
const WelcomeBackModal = ({ isOpen, onClose, user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lastSession, setLastSession] = useState(null);
  const [hasActiveSessions, setHasActiveSessions] = useState(false);
  const [openingPlayer, setOpeningPlayer] = useState(false);
  
  // Credit gating hooks
  const { balance } = useReaderCredits();
  const { setShowCreditExpiredModal, isBetaTester } = useReaderFunnel();

  useEffect(() => {
    if (isOpen && user) {
      checkActiveSessions();
    }
  }, [isOpen, user]);

  const checkActiveSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.get('/reader/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const continueListening = response.data?.continue_listening || [];
      
      if (continueListening.length > 0) {
        // Get the most recent session
        const mostRecent = continueListening[0];
        setLastSession(mostRecent);
        setHasActiveSessions(true);
      } else {
        setHasActiveSessions(false);
        // No active sessions - auto redirect to home after brief delay
        setTimeout(() => {
          onClose();
          navigate('/home');
        }, 500);
      }
    } catch (error) {
      console.error('[WelcomeBack] Error checking sessions:', error);
      // On error, just go to home
      onClose();
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueReading = async () => {
    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[WELCOME BACK] Continue Listening BLOCKED: No credits remaining (balance:', balance, ')');
      // CreditExpiredModal handles the messaging - no toast needed
      onClose();
      setShowCreditExpiredModal(true);
      return;
    }
    
    // Check if bookmark is less than 24 hours old
    const bookmarkTimestamp = lastSession?.bookmark?.timestamp || lastSession?.last_interaction;
    let hoursSinceBookmark = 999; // Default to > 24 hours if no timestamp
    
    if (bookmarkTimestamp) {
      const lastInteractionTime = new Date(bookmarkTimestamp).getTime();
      const now = Date.now();
      hoursSinceBookmark = (now - lastInteractionTime) / (1000 * 60 * 60);
      console.log('[WELCOME BACK] Bookmark age:', hoursSinceBookmark.toFixed(2), 'hours');
    }
    
    // If bookmark is < 24 hours old, go directly to player (skip recap modal)
    if (hoursSinceBookmark < 24 && lastSession?.manuscript_id) {
      console.log('[WELCOME BACK] Bookmark < 24 hours - going directly to player');
      setOpeningPlayer(true);
      
      try {
        const token = localStorage.getItem('accessToken');
        const result = await apiFetch(`${BACKEND_URL}/api/manuscripts/${lastSession.manuscript_id}/open`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!result.ok) {
          console.error('[WELCOME BACK] Failed to open manuscript:', result.error);
          // Fallback to library page with recap
          onClose();
          navigate('/reader/my-library', { 
            state: { 
              showRecapFor: lastSession.manuscript_id,
              autoOpenRecap: true 
            } 
          });
          return;
        }
        
        const { session_id } = result.json;
        onClose();
        
        // Build bookmark object from lastSession.last_position
        const bookmark = lastSession.last_position ? {
          chapterIndex: lastSession.last_position.current_chapter_index || 0,
          chunkIndex: lastSession.last_position.current_chunk_index || 0,
          positionSeconds: lastSession.last_position.position_seconds || 0,
          timestamp: lastSession.last_interaction
        } : null;
        
        // Navigate directly to player - resume from bookmark, no recap prompt
        navigate(`/player/${session_id}`, {
          state: {
            returnTo: '/reader/my-library',
            role: 'reader',
            showRecapPrompt: false,
            startFromBeginning: false,
            bookmark: bookmark
          }
        });
      } catch (error) {
        console.error('[WELCOME BACK] Error opening player:', error);
        setOpeningPlayer(false);
        // Fallback to library page
        onClose();
        navigate('/reader/my-library');
      }
      return;
    }
    
    // Bookmark >= 24 hours old - go to library with recap modal
    console.log('[WELCOME BACK] Bookmark >= 24 hours - showing recap modal');
    onClose();
    navigate('/reader/my-library', { 
      state: { 
        showRecapFor: lastSession?.manuscript_id,
        autoOpenRecap: true 
      } 
    });
  };

  const handleGoHome = () => {
    onClose();
    navigate('/home');
  };

  // Don't render if no active sessions (will auto-redirect)
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] sm:max-w-md bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 border-slate-700 p-0 overflow-hidden mx-auto">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Welcome Back!</h2>
              <p className="text-sm text-indigo-100">
                {user?.first_name || user?.email?.split('@')[0] || 'Reader'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mb-3" />
              <p className="text-slate-400 text-sm">Checking your reading progress...</p>
            </div>
          ) : hasActiveSessions && lastSession ? (
            <div className="space-y-4">
              {/* Last session info with cover */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                <p className="text-xs uppercase tracking-wider text-indigo-400 mb-3">
                  Pick up where you left off
                </p>
                
                <div className="flex gap-4">
                  {/* Book Cover */}
                  <div className="flex-shrink-0">
                    <img
                      src={lastSession.manuscript_id 
                        ? `${BACKEND_URL}/api/covers/${lastSession.manuscript_id}`
                        : '/newaudia-logo.jpg'
                      }
                      alt={lastSession.bookTitle || lastSession.title}
                      className="w-20 h-28 sm:w-24 sm:h-32 object-cover rounded-lg shadow-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/newaudia-logo.jpg';
                      }}
                    />
                  </div>
                  
                  {/* Book Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-semibold text-white mb-1 line-clamp-2">
                      {lastSession.bookTitle || lastSession.title}
                    </p>
                    <p className="text-sm text-slate-400 mb-3">
                      by {lastSession.author}
                    </p>
                    
                    {lastSession.progressPercent > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-slate-400 mb-1">
                          <span>Progress</span>
                          <span>{Math.round(lastSession.progressPercent)}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-full transition-all"
                            style={{ width: `${lastSession.progressPercent}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Question */}
              <p className="text-center text-slate-300">
                Would you like to continue listening?
              </p>

              {/* Action buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleContinueReading}
                  disabled={openingPlayer}
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-indigo-500/25 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {openingPlayer ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Opening...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      Continue Listening
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleGoHome}
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-slate-700 hover:bg-slate-600 text-slate-100 font-medium rounded-xl transition-all"
                >
                  <Home className="w-5 h-5" />
                  Browse Homepage
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6">
              <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
              <p className="text-slate-400 text-sm mt-2">Redirecting to homepage...</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeBackModal;
